import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Play } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useProgress } from '../hooks/useProgress'
import { LESSON_INDEX } from '../content/lessonIndex'
import { ModulePanel } from '../components/map/ModulePanel'
import { useCheckout } from '../hooks/useCheckout'
import { UpgradePanel } from '../components/map/UpgradePanel'
import { PaywallModal } from '../components/map/PaywallModal'
import { TIERS } from '../lib/stripe'
import { CONTENT_FLAGS } from '../lib/contentFlags'
import {
  ROLE_NAMES, FOUNDATION_MODULES, TREE_IDS, TREE_LABELS,
  TREE_COLORS, CAPSTONE_NAMES,
  findResumeLesson, findGlobalResume,
} from '../lib/universeMap'
import './UniverseMap.css'

export function UniverseMap() {
  const { profile, refreshProfile } = useAuth()
  const { progress, stats, isLessonComplete } = useProgress()
  const navigate = useNavigate()

  const { startCheckout, loading: checkoutLoading } = useCheckout()
  const [panel,   setPanel]   = useState(null)
  const [upgrade, setUpgrade] = useState(null)
  const [paywall, setPaywall] = useState(null)   // { lessonTitle } | null
  const [toast,   setToast]   = useState(null)

  const location = useLocation()

  // Handle Stripe checkout return
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const result = params.get('checkout')

    if (result === 'success') {
      setToast({ type: 'success', message: 'Payment successful. Your access has been activated.' })
      window.history.replaceState({}, '', '/dashboard')

      // Self-scheduling poll. Using setInterval here would fire a second
      // refreshProfile() while the first is still in flight if the
      // network gets slow; this version waits for the previous call
      // to settle before queueing the next.
      let cancelled = false
      let timeoutId = null
      let attempts  = 0

      const tick = async () => {
        if (cancelled) return
        attempts++
        try { await refreshProfile() } catch (_) { /* ignore */ }
        if (!cancelled && attempts < 8) {
          timeoutId = setTimeout(tick, 1000)
        }
      }
      tick()

      return () => {
        cancelled = true
        if (timeoutId) clearTimeout(timeoutId)
      }

    } else if (result === 'cancelled') {
      setToast({ type: 'info', message: 'Checkout cancelled. No payment was taken.' })
      window.history.replaceState({}, '', '/dashboard')
    }
  }, [location.search, refreshProfile])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(null), 5000)
    return () => clearTimeout(t)
  }, [toast])

  const firstName     = profile?.full_name?.split(' ')[0] || 'there'
  const subTier       = profile?.subscription_tier || 0
  const subStatus     = profile?.subscription_status || 'inactive'
  const isSubActive   = subStatus === 'active'
  const unlockedTrees = profile?.unlocked_trees || []
  const selectedTree  = profile?.selected_tree || 'infrastructure'

  const foundationComplete =
    stats.itFoundation.completed === stats.itFoundation.total &&
    stats.humanSkills.completed  === stats.humanSkills.total  &&
    stats.aiFoundations.completed === stats.aiFoundations.total &&
    stats.itFoundation.total > 0

  const globalResume = useMemo(
    () => findGlobalResume(LESSON_INDEX, progress.completedLessons),
    [progress.completedLessons]
  )
  const itResume = useMemo(() => findResumeLesson(LESSON_INDEX, progress.completedLessons, 'it'), [progress.completedLessons])
  const hsResume = useMemo(() => findResumeLesson(LESSON_INDEX, progress.completedLessons, 'hs'), [progress.completedLessons])
  const aiResume = useMemo(() => findResumeLesson(LESSON_INDEX, progress.completedLessons, 'ai'), [progress.completedLessons])

  // Resume button on the header — gate behind paywall too
  const handleHeaderResume = useCallback(() => {
    if (!globalResume) return
    if (!isSubActive) {
      setPaywall({ lessonTitle: globalResume.title })
      return
    }
    navigate(`/learn/${globalResume.slug}`)
  }, [globalResume, isSubActive, navigate])

  // Capstone CTA on the map — gate behind paywall too
  const handleCapstoneClick = useCallback(() => {
    if (!isSubActive) {
      setPaywall({ lessonTitle: 'Foundation Capstone — Getting Real' })
      return
    }
    navigate('/learn/foundation-capstone-getting-real')
  }, [isSubActive, navigate])

  const buildFoundationSections = useCallback((track) => {
    const mods = FOUNDATION_MODULES[track]
    const lessons = LESSON_INDEX.filter(l => l.track === track && l.type !== 'capstone')
    return [{
      heading: null,
      modules: mods.map(mod => ({
        num: mod.num,
        title: mod.title,
        lessons: lessons
          .filter(l => l.module === mod.module)
          .sort((a, b) => a.position - b.position)
          .map(l => ({
            id:        l.id,
            slug:      l.slug,
            title:     l.title,
            done:      isLessonComplete(l.id),
            isRef:     l.type === 'ref',
            isIntro:   l.type === 'intro',
            isCurrent: !isLessonComplete(l.id) &&
              (track === 'it' ? l.id === itResume?.id :
               track === 'hs' ? l.id === hsResume?.id :
               l.id === aiResume?.id),
          }))
          .filter(l => !l.isIntro),
      })),
    }]
  }, [isLessonComplete, itResume, hsResume, aiResume])

  const foundationSections = useMemo(() => {
    return ['it', 'hs', 'ai'].map(track => ({
      heading: { it: 'IT Foundation', hs: 'Human Skills', ai: 'AI Foundations' }[track],
      modules: buildFoundationSections(track)[0].modules,
    }))
  }, [buildFoundationSections])

  const openFoundationPanel = useCallback(() => {
    setPanel({
      eyebrow: 'Foundation', title: 'Foundation Complete', color: 'teal',
      sections: foundationSections,
      totalLessons:   stats.itFoundation.total + stats.humanSkills.total + stats.aiFoundations.total,
      completedCount: stats.itFoundation.completed + stats.humanSkills.completed + stats.aiFoundations.completed,
      resumeLesson: null,
      credentialId: progress.credentialId,
    })
    setUpgrade(null)
  }, [foundationSections, stats, progress.credentialId])

  const openTrackPanel = useCallback((track) => {
    const cfgs = {
      it: { eyebrow: 'IT Foundation', title: 'IT Foundation', color: 'accent',
            totalLessons: stats.itFoundation.total, completedCount: stats.itFoundation.completed, resumeLesson: itResume },
      hs: { eyebrow: 'Human Skills', title: 'Human Skills Foundation', color: 'teal',
            totalLessons: stats.humanSkills.total, completedCount: stats.humanSkills.completed, resumeLesson: hsResume },
      ai: { eyebrow: 'AI Foundations', title: 'AI Foundations', color: 'indigo',
            totalLessons: stats.aiFoundations.total, completedCount: stats.aiFoundations.completed, resumeLesson: aiResume },
    }
    const cfg = cfgs[track]
    if (!cfg) return
    setPanel({ ...cfg, sections: buildFoundationSections(track), credentialId: null })
    setUpgrade(null)
  }, [stats, itResume, hsResume, aiResume, buildFoundationSections])

  const openUpgrade = useCallback((mode, tier, treeId) => {
    setUpgrade({ mode, tier, treeId })
    setPanel(null)
  }, [])

  const closeAll = useCallback(() => { setPanel(null); setUpgrade(null) }, [])
  const closePaywall = useCallback(() => setPaywall(null), [])

  const showLessonPaywall = useCallback((lessonTitle) => {
    setPaywall({ lessonTitle })
  }, [])

  const headerTitle = useMemo(() => {
    if (!foundationComplete) return 'Foundation in progress.'
    return `${TREE_LABELS[selectedTree] || 'your career'} — in progress.`
  }, [foundationComplete, selectedTree])

  const isTreeActive   = id => id === selectedTree && subTier >= 2
  const isTreeUnlocked = id => unlockedTrees.includes(id) && subTier >= 2 && id !== selectedTree
  const isTierLocked   = t  => subTier < t

  const T2_CONTENT_LIVE = CONTENT_FLAGS.T2_CONTENT_LIVE
  const T3_CONTENT_LIVE = CONTENT_FLAGS.T3_CONTENT_LIVE
  const T4_CONTENT_LIVE = CONTENT_FLAGS.T4_CONTENT_LIVE

  const capstonState = (id, tier) => {
    if (tier === 1) {
      if (progress.capstonePassed) return 'passed'
      if (foundationComplete) return 'ready'
      return 'locked'
    }
    if (tier === 2 && T2_CONTENT_LIVE && (isTreeActive(id) || isTreeUnlocked(id))) return 'locked'
    if (tier === 3 && T3_CONTENT_LIVE && (isTreeActive(id) || isTreeUnlocked(id))) return 'locked'
    if (tier === 4 && T4_CONTENT_LIVE && (isTreeActive(id) || isTreeUnlocked(id))) return 'locked'
    return 'coming'
  }

  return (
    <div className="universe-map">

      {/* Header */}
      <div className="umap-header">
        <div>
          <div className="umap-header__eyebrow mono">
            {firstName} · Tier {subTier || 1} · {isSubActive ? 'Active' : 'Foundation'}
          </div>
          <h1 className="umap-header__title">{headerTitle}</h1>
        </div>
        {globalResume && (
          <button className="umap-resume" onClick={handleHeaderResume}>
            <Play size={14} />
            Resume — {globalResume.title}
          </button>
        )}
      </div>

      {/* Subscription CTA — shown whenever subscription is not active */}
      {!isSubActive && (
        <div style={{
          background: 'var(--surface)', border: '1px solid rgba(232,255,71,0.2)',
          borderRadius: '16px', padding: '28px 32px', marginBottom: '24px',
          position: 'relative', overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
            background: 'var(--accent)'
          }} />
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--accent)', marginBottom: '8px' }}>
            {subStatus === 'paused' ? 'Resume your subscription'
             : subStatus === 'cancelled' ? 'Reactivate your subscription'
             : 'Start your subscription'}
          </div>
          <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text)', marginBottom: '6px', letterSpacing: '-0.02em' }}>
            Foundation — {TIERS[1].label}
          </div>
          <div style={{ fontSize: '0.85rem', color: 'var(--text-2)', marginBottom: '20px', lineHeight: 1.6 }}>
            {TIERS[1].firstMonthLabel} your first month. Access IT Foundation, Human Skills, and AI Foundations — all three tracks, 96 lessons, one capstone.
          </div>
          <div style={{
            fontFamily: 'var(--font-mono)', fontSize: '0.6rem',
            color: 'var(--text-3)', marginBottom: '14px', lineHeight: 1.55,
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '8px', padding: '10px 12px'
          }}>
            By subscribing, you agree to the{' '}
            <a href="https://techbench.it/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--accent2)', textDecoration: 'none' }}>Terms</a>,{' '}
            <a href="https://techbench.it/user-agreement" target="_blank" rel="noreferrer" style={{ color: 'var(--accent2)', textDecoration: 'none' }}>User Agreement</a>, and{' '}
            <a href="https://techbench.it/refund-policy" target="_blank" rel="noreferrer" style={{ color: 'var(--accent2)', textDecoration: 'none' }}>Refund Policy</a>.
            You consent to immediate content delivery and waive your 14-day cooling-off right.
          </div>
          <button
            onClick={() => startCheckout({ type: 'subscription_t1' })}
            disabled={checkoutLoading}
            style={{
              background: 'var(--accent)', color: '#0a0c10', border: 'none',
              borderRadius: '10px', fontFamily: 'var(--font-sans)', fontWeight: 700,
              fontSize: '0.9rem', padding: '12px 28px', cursor: checkoutLoading ? 'wait' : 'pointer',
              transition: 'all 150ms ease', opacity: checkoutLoading ? 0.7 : 1
            }}
          >
            {checkoutLoading ? 'Redirecting...' : `Subscribe and start — ${TIERS[1].firstMonthLabel}`}
          </button>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: 'var(--text-3)', marginTop: '10px' }}>
            Cancel anytime. Pause and resume. One-way progression — see <a href="https://techbench.it/how-it-works" style={{ color: 'var(--accent2)', textDecoration: 'none' }}>how it works</a>.
          </div>
        </div>
      )}

      <div className="umap-grid">

        {/* ══ TIER 1 — FOUNDATION ══ */}
        <div className="tier-section">
          <div className="tier-label-bar">
            <span className="tier-label-bar__num mono">Tier 1</span>
            <span className="tier-label-bar__name">Foundation</span>
            <div className="tier-label-bar__line" />
          </div>

          {foundationComplete ? (
            <>
              <button className="foundation-bar" onClick={openFoundationPanel}>
                <div className="foundation-bar__check">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <div className="foundation-bar__info">
                  <div className="foundation-bar__title">Foundation complete</div>
                  <div className="foundation-bar__meta mono">
                    {stats.itFoundation.total + stats.humanSkills.total + stats.aiFoundations.total} lessons · IT Foundation · Human Skills · AI Foundations
                  </div>
                </div>
                {progress.credentialId && (
                  <div className="foundation-bar__cred mono">{progress.credentialId}</div>
                )}
                <div className="foundation-bar__hint mono">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>
                  Browse lessons
                </div>
              </button>
              <div className="capstone-row capstone-row--foundation">
                <CapstoneCell
                  state={capstonState(null, 1)}
                  tierLabel="Foundation Capstone"
                  name={CAPSTONE_NAMES.foundation}
                  credentialId={progress.credentialId}
                  onClick={handleCapstoneClick}
                />
              </div>
            </>
          ) : (
            <>
              <div className="shared-row">
                <TrackCell eyebrow="Human Skills" title="Human Skills Foundation" color="teal"
                  completed={stats.humanSkills.completed} total={stats.humanSkills.total}
                  onClick={() => openTrackPanel('hs')} />
                <TrackCell eyebrow="AI Foundations" title="AI Foundations" color="indigo"
                  completed={stats.aiFoundations.completed} total={stats.aiFoundations.total}
                  onClick={() => openTrackPanel('ai')} />
              </div>
              <div className="tree-row tree-row--foundation">
                <TrackCell eyebrow="IT Foundation" title="IT Foundation Ready" color="accent"
                  completed={stats.itFoundation.completed} total={stats.itFoundation.total}
                  onClick={() => openTrackPanel('it')} />
              </div>
              <div className="capstone-row capstone-row--foundation">
                <CapstoneCell
                  state={capstonState(null, 1)}
                  tierLabel="Foundation Capstone"
                  name={CAPSTONE_NAMES.foundation}
                  credentialId={null}
                  onClick={handleCapstoneClick}
                />
              </div>
            </>
          )}
        </div>

        {/* ══ TIER 2 ══ */}
        <TierSection tierNum={2} tierName="Entry Level" isLocked={isTierLocked(2)}>
          <div className="shared-row">
            <ComingSoonCell eyebrow="Human Skills" title={ROLE_NAMES.t2.hs} onClick={() => openUpgrade('coming-soon', 2, selectedTree)} />
            <ComingSoonCell eyebrow="AI Foundations" title={ROLE_NAMES.t2.ai} onClick={() => openUpgrade('coming-soon', 2, selectedTree)} />
          </div>
          <div className="tree-row tree-row--trees">
            {TREE_IDS.map(id => {
              const color = TREE_COLORS[id]
              const isActive   = isTreeActive(id)
              const isUnlocked = isTreeUnlocked(id)
              if (T2_CONTENT_LIVE && (isActive || isUnlocked)) {
                return (
                  <ActiveTreeCell key={id}
                    eyebrow={isActive ? 'Primary tree' : 'Branch unlock'}
                    title={ROLE_NAMES.t2[id]} color={color}
                    completed={0} total={isActive ? 48 : 44}
                    onClick={() => openUpgrade('active', 2, id)} />
                )
              }
              return (
                <ComingSoonCell key={id} eyebrow={TREE_LABELS[id]} title={ROLE_NAMES.t2[id]}
                  color={color} onClick={() => openUpgrade('coming-soon', 2, id)} />
              )
            })}
          </div>
          <div className="capstone-row capstone-row--trees">
            {TREE_IDS.map(id => (
              <CapstoneCell key={id}
                state={capstonState(id, 2)}
                tierLabel="T2 Capstone"
                name={CAPSTONE_NAMES.t2[id]}
                onClick={() => openUpgrade('coming-soon', 2, id)} />
            ))}
          </div>
        </TierSection>

        {/* ══ TIER 3 ══ */}
        <TierSection tierNum={3} tierName="Mid-Level" isLocked={isTierLocked(3)}>
          <div className="shared-row">
            <ComingSoonCell eyebrow="Human Skills" title={ROLE_NAMES.t3.hs} onClick={() => openUpgrade('coming-soon', 3, selectedTree)} />
            <ComingSoonCell eyebrow="AI Foundations" title={ROLE_NAMES.t3.ai} onClick={() => openUpgrade('coming-soon', 3, selectedTree)} />
          </div>
          <div className="tree-row tree-row--trees">
            {TREE_IDS.map(id => (
              <ComingSoonCell key={id} eyebrow={TREE_LABELS[id]} title={ROLE_NAMES.t3[id]}
                color={TREE_COLORS[id]} onClick={() => openUpgrade('coming-soon', 3, id)} />
            ))}
          </div>
          <div className="capstone-row capstone-row--trees">
            {TREE_IDS.map(id => (
              <CapstoneCell key={id} state="coming" tierLabel="T3 Capstone"
                name={CAPSTONE_NAMES.t3[id]} onClick={() => openUpgrade('coming-soon', 3, id)} />
            ))}
          </div>
        </TierSection>

        {/* ══ TIER 4 ══ */}
        <TierSection tierNum={4} tierName="Senior" isLocked={isTierLocked(4)}>
          <div className="shared-row">
            <ComingSoonCell eyebrow="Human Skills" title={ROLE_NAMES.t4.hs} onClick={() => openUpgrade('coming-soon', 4, selectedTree)} />
            <ComingSoonCell eyebrow="AI Foundations" title={ROLE_NAMES.t4.ai} onClick={() => openUpgrade('coming-soon', 4, selectedTree)} />
          </div>
          <div className="tree-row tree-row--trees">
            {TREE_IDS.map(id => (
              <ComingSoonCell key={id} eyebrow={TREE_LABELS[id]} title={ROLE_NAMES.t4[id]}
                color={TREE_COLORS[id]} onClick={() => openUpgrade('coming-soon', 4, id)} />
            ))}
          </div>
          <div className="capstone-row capstone-row--trees">
            {TREE_IDS.map(id => (
              <CapstoneCell key={id} state="coming" tierLabel="T4 Capstone"
                name={CAPSTONE_NAMES.t4[id]} onClick={() => openUpgrade('coming-soon', 4, id)} />
            ))}
          </div>
        </TierSection>

      </div>

      {/* Panels */}
      {panel && (
        <ModulePanel
          isOpen={!!panel} onClose={closeAll}
          eyebrow={panel.eyebrow} title={panel.title} color={panel.color}
          sections={panel.sections} resumeLesson={panel.resumeLesson}
          totalLessons={panel.totalLessons} completedCount={panel.completedCount}
          credentialId={panel.credentialId}
          onCertClick={() => { closeAll(); navigate('/certificate') }}
          isSubActive={isSubActive}
          onLockedLessonClick={showLessonPaywall}
        />
      )}
      <UpgradePanel
        isOpen={!!upgrade} onClose={closeAll}
        mode={upgrade?.mode} tier={upgrade?.tier} treeId={upgrade?.treeId}
      />

      {/* Paywall modal */}
      <PaywallModal
        isOpen={!!paywall}
        onClose={closePaywall}
        lessonTitle={paywall?.lessonTitle}
        subStatus={subStatus}
      />

      {/* Toast — used only for checkout success/cancel now */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '28px', left: '50%',
          transform: 'translateX(-50%)',
          background: toast.type === 'success' ? 'var(--teal-dim, rgba(0,201,167,0.12))' : 'var(--surface-2, #161b24)',
          border: `1px solid ${toast.type === 'success' ? 'rgba(0,201,167,0.3)' : 'var(--border-2, rgba(255,255,255,0.12))'}`,
          borderRadius: '10px', padding: '12px 20px',
          fontFamily: 'var(--font-mono)', fontSize: '0.75rem',
          color: toast.type === 'success' ? 'var(--teal, #00c9a7)' : 'var(--text-2, #6b7280)',
          zIndex: 300, whiteSpace: 'nowrap',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          animation: 'slideUp 0.3s ease'
        }}>
          {toast.message}
        </div>
      )}
      <style>{`@keyframes slideUp { from { opacity:0; transform:translateX(-50%) translateY(12px) } to { opacity:1; transform:translateX(-50%) translateY(0) } }`}</style>
    </div>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function TierSection({ tierNum, tierName, isLocked, children }) {
  return (
    <>
      <div className="map-divider">
        <div className="map-divider__label mono">{isLocked ? `Tier ${tierNum}` : 'Active'}</div>
        <div className="map-divider__line" />
      </div>
      <div className={`tier-section ${isLocked ? 'tier-section--locked' : ''}`}>
        <div className="tier-label-bar">
          <span className="tier-label-bar__num mono">Tier {tierNum}</span>
          <span className="tier-label-bar__name">{tierName}</span>
          <div className="tier-label-bar__line" />
        </div>
        {children}
      </div>
    </>
  )
}

function TrackCell({ eyebrow, title, color, completed, total, onClick }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0
  return (
    <button className={`map-cell map-cell--${color}`} onClick={onClick}>
      <div className={`map-cell__eyebrow eyebrow--${color}`}>{eyebrow}</div>
      <div className="map-cell__name">{title}</div>
      <div className="map-cell__pr-row">
        <span className="map-cell__count mono">{completed} / {total} lessons</span>
        <span className={`map-cell__pct mono pct--${color}`}>{pct}%</span>
      </div>
      <div className="map-cell__bar">
        <div className={`map-cell__fill fill--${color}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="map-cell__hint mono">
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/>
        </svg>
        View modules
      </div>
    </button>
  )
}

function ActiveTreeCell({ eyebrow, title, color, completed, total, onClick }) {
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0
  return (
    <button className={`map-cell map-cell--${color}`} onClick={onClick}>
      <div className="map-cell__eyebrow eyebrow--accent">{eyebrow}</div>
      <div className="map-cell__name">{title}</div>
      <div className="map-cell__pr-row">
        <span className="map-cell__count mono">{completed} / {total} lessons</span>
        <span className={`map-cell__pct mono pct--${color}`}>{pct}%</span>
      </div>
      <div className="map-cell__bar">
        <div className={`map-cell__fill fill--${color}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="map-cell__hint mono">
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/>
        </svg>
        View modules
      </div>
    </button>
  )
}

function ComingSoonCell({ eyebrow, title, color, onClick }) {
  return (
    <button className={`map-cell map-cell--coming-soon ${color ? `map-cell--coming-${color}` : ''}`} onClick={onClick}>
      <div className="map-cell__eyebrow eyebrow--locked">{eyebrow}</div>
      <div className="map-cell__name">{title}</div>
      <div className="map-cell__coming-tag mono">Coming soon</div>
    </button>
  )
}

function CapstoneCell({ state, tierLabel, name, credentialId, onClick }) {
  const isPassed = state === 'passed'
  const isReady  = state === 'ready'
  const isComing = state === 'coming'
  const isLocked = state === 'locked'

  return (
    <button
      className={`map-cell map-capstone map-capstone--${state}`}
      onClick={onClick}
      disabled={isLocked && !isReady}
    >
      <div className="map-capstone__eyebrow mono">{tierLabel}</div>
      <div className="map-capstone__name">{name}</div>
      {isPassed && (
        <div className="map-capstone__sub mono">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          Passed{credentialId ? ` · ${credentialId}` : ''}
        </div>
      )}
      {isReady && (
        <div className="map-capstone__sub map-capstone__sub--ready mono">Ready — take it now</div>
      )}
      {isLocked && (
        <div className="map-capstone__sub mono">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          Complete all modules
        </div>
      )}
      {isComing && (
        <div className="map-capstone__sub mono">Coming soon</div>
      )}
    </button>
  )
}
